import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useEventSource } from '../../../lib/useEventSource'
import { useAuthStore } from '../../../stores/authStore'
import { useNotificationStore } from '../../../stores/notificationStore'
import type { ActivityGroup, ActivitiesResponse } from '../api/activityApi'

// FIX-VALIDATION-F014: Throttle SSE cache updates to max 1 per 6 seconds
// This prevents a flood of SSE events from causing excessive React re-renders
// AC-3: max <10 req/60s → 60s/6s = 10 req/min (boundary)
const SSE_THROTTLE_MS = 6000

export function useActivityStream() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('activity')
  const addNotification = useNotificationStore((state) => state.addNotification)
  const accessToken = useAuthStore((state) => state.accessToken)

  // FIX-VALIDATION-F014: Track last SSE update for throttling
  const lastSseUpdateRef = useRef<number>(0)

  // Ob der SSE-Kanal blockiert werden soll (nach server-seitigem error-Event).
  // State statt Ref: das Blockieren MUSS einen Re-Render auslösen, damit enabled
  // sofort kippt und useEventSource die Verbindung schließt (mit Ref griff der
  // Block erst beim nächsten fremden Render — und Refs im Render sind unzulässig).
  const [sseBlocked, setSseBlocked] = useState(false)

  const { status, error, reconnect } = useEventSource<ActivityGroup>(
    '/api/v2/streams/activities',
    {
      // Nur verbinden wenn Token vorhanden — EventSource kann keine Header senden,
      // Token wird von useEventSource als ?access_token=<token> Query-Param angehängt.
      // Bei fehlendem Token: kein Verbindungsversuch, kein Reconnect-Spam.
      enabled: !!accessToken && !sseBlocked,
      name: 'activities',
      events: {
        activity: (newGroup) => {
          const now = Date.now()
          const timeSinceLastUpdate = now - lastSseUpdateRef.current

          // Throttle SSE cache updates to prevent request storm
          if (timeSinceLastUpdate >= SSE_THROTTLE_MS) {
            lastSseUpdateRef.current = now

            queryClient.setQueriesData<{ data?: ActivitiesResponse; error?: unknown; status?: number; fromMock?: boolean }>(
              { queryKey: ['activities'] },
              (oldData) => {
                if (!oldData || !oldData.data) return oldData

                // Nur prependen wenn group_key noch nicht vorhanden (Deduplizierung)
                const exists = oldData.data.data.some(
                  (g) => g.group_key === newGroup.group_key
                )
                if (exists) return oldData

                return {
                  ...oldData,
                  data: {
                    ...oldData.data,
                    data: [newGroup, ...oldData.data.data],
                  },
                }
              }
            )

            // Notifications-Query invalidieren → NotificationsPage zeigt neue Einträge
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
          }

          // Push notification (immer, nicht throttled)
          addNotification({
            severity: 'info',
            title: t('stream.newActivityTitle'),
            body: newGroup.human_label,
          })
        },
        // error-Event vom Server (z.B. Unauthorized): sauber schließen, kein Reconnect-Hammer.
        // Der Handler empfängt die geparsten Daten — wir ignorieren den Inhalt und blockieren
        // weitere Verbindungsversuche (State-Update kippt enabled und schließt die Verbindung).
        error: (_payload: unknown) => {
          setSseBlocked(true)
        },
      },
    }
  )

  return {
    connected: status === 'open',
    error,
    reconnect,
  }
}
